import React from 'react';
import { Button } from '../ui/button';
import { ArrowRight, Download, Sparkles } from 'lucide-react';
import { companyInfo, stats } from '../../data/mock';

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-purple-50">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#6B2C91]/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#FFD700]/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#0066CC]/5 rounded-full blur-3xl" />
      </div>

      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%236B2C91' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#6B2C91]/10 rounded-full">
              <Sparkles className="w-4 h-4 text-[#6B2C91]" />
              <span className="text-sm font-medium text-[#6B2C91]">Filiale EKIP Holding Company</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              KIPAY
              <span className="block text-[#6B2C91]">{companyInfo.tagline}</span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl text-gray-600 leading-relaxed max-w-xl">
              {companyInfo.description}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                className="bg-[#6B2C91] hover:bg-[#5a2579] text-white px-8 py-6 text-lg group"
              >
                Commander Ma Carte
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-[#6B2C91] text-[#6B2C91] hover:bg-[#6B2C91]/10 px-8 py-6 text-lg"
              >
                <Download className="mr-2 w-5 h-5" />
                Télécharger l'App
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t border-gray-200">
              {stats.map((stat, index) => (
                <div key={index} className="text-center sm:text-left">
                  <div className="text-3xl font-bold text-[#6B2C91]">{stat.value}</div>
                  <div className="text-sm font-medium text-gray-900">{stat.label}</div>
                  <div className="text-xs text-gray-500">{stat.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Card Visual */}
          <div className="relative hidden lg:block">
            <div className="relative">
              {/* Floating cards effect */}
              <div className="absolute -top-10 -left-10 w-72 h-44 bg-gradient-to-br from-gray-400 to-gray-500 rounded-2xl shadow-2xl transform rotate-[-15deg] opacity-60" />
              <div className="absolute top-5 left-5 w-72 h-44 bg-gradient-to-br from-slate-600 to-slate-700 rounded-2xl shadow-2xl transform rotate-[-8deg] opacity-80" />
              
              {/* Main Gold Card */}
              <div className="relative w-80 h-48 bg-gradient-to-br from-amber-400 via-yellow-400 to-amber-500 rounded-2xl shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500">
                {/* Card content */}
                <div className="absolute inset-0 p-6 flex flex-col justify-between">
                  <div className="flex justify-between items-start">
                    <img src={companyInfo.logo} alt="KIPAY" className="h-10" />
                    <span className="text-white text-sm font-semibold bg-white/20 px-3 py-1 rounded-full">GOLD</span>
                  </div>
                  <div>
                    <div className="text-white/80 text-xs mb-1">CARTE KIPAY</div>
                    <div className="text-white font-mono text-lg tracking-wider">•••• •••• •••• 4589</div>
                  </div>
                </div>
                {/* Holographic effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-transparent via-white/20 to-transparent" />
              </div>

              {/* Phone mockup */}
              <div className="absolute -right-10 bottom-0 w-48 h-80 bg-gray-900 rounded-3xl shadow-2xl border-4 border-gray-800 overflow-hidden transform translate-y-10">
                <div className="h-6 bg-gray-900 flex items-center justify-center">
                  <div className="w-20 h-4 bg-gray-800 rounded-full" />
                </div>
                <div className="bg-white h-full p-3">
                  <div className="text-center mb-3">
                    <div className="w-12 h-12 mx-auto bg-[#6B2C91]/10 rounded-full flex items-center justify-center">
                      <span className="text-[#6B2C91] font-bold text-xl">K</span>
                    </div>
                    <div className="text-xs font-semibold text-gray-900 mt-1">KIPAY</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 mb-2">
                    <div className="text-xs text-gray-500">Vos Points</div>
                    <div className="text-2xl font-bold text-[#6B2C91]">12,450</div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-8 bg-[#00C853]/10 rounded-lg flex items-center px-2">
                      <span className="text-xs text-[#00C853]">+150 pts</span>
                    </div>
                    <div className="h-8 bg-gray-100 rounded-lg" />
                    <div className="h-8 bg-gray-100 rounded-lg" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-8 h-12 border-2 border-gray-400 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-gray-400 rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
